import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, ExternalLink, KeyRound, Trash2 } from "lucide-react";
import AdminLayout from "../../platform/AdminLayout";
import { api } from "../../lib/api";
import PagePreview from "../../components/PagePreview";
import ButtonsEditor from "../../components/ButtonsEditor";
import { PeriodPicker, Bars, StatCard, dayPlus } from "../../components/stats";

const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-3.5 py-2.5 text-sm text-nuit-900 placeholder:text-steel/60 focus:border-electric focus:outline-none";

const STATUS_LABEL = { actif: "Actif", inactif: "Inactif", archive: "Archivé" };

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("fiche");
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [tempPwd, setTempPwd] = useState("");

  const load = useCallback(() => {
    api(`/admin/clients/${clientId}`).then(setClient).catch((e) => setError(e.message));
  }, [clientId]);
  useEffect(load, [load]);

  const patch = async (body, okMsg = "Modifications enregistrées.") => {
    setError("");
    setSaved("");
    try {
      await api(`/admin/clients/${clientId}`, { method: "PATCH", body });
      setSaved(okMsg);
      load();
      setTimeout(() => setSaved(""), 4000);
    } catch (e) {
      setError(e.message); // les données saisies sont conservées (pas de rechargement)
    }
  };

  const setStatus = async (status) => {
    if (status === "archive" && !window.confirm("Archiver ce client ? Sa fiche, ses statistiques et son URL NFC sont conservées. Sa page publique reste en ligne sauf si vous la désactivez séparément.")) return;
    if (status === "inactif" && !window.confirm("Désactiver le compte commerçant ? Il ne pourra plus se connecter, mais sa page NFC reste en ligne.")) return;
    setError("");
    try {
      await api(`/admin/clients/${clientId}/status`, { method: "POST", body: { status } });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const resetAccess = async () => {
    if (!window.confirm("Réinitialiser l'accès commerçant ? Un nouveau mot de passe provisoire sera généré et envoyé par e-mail.")) return;
    setError("");
    try {
      const data = await api(`/admin/clients/${clientId}/reset-access`, { method: "POST" });
      setTempPwd(data.temp_password);
    } catch (e) {
      setError(e.message);
    }
  };

  const copyUrl = () => {
    navigator.clipboard?.writeText(client.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!client) {
    return (
      <AdminLayout>
        {error ? <p className="rounded-xl bg-red-50 p-4 text-sm font-medium text-red-700">{error}</p>
          : <p className="text-sm text-steel">Chargement de la fiche…</p>}
      </AdminLayout>
    );
  }

  const TABS = [
    ["fiche", "Fiche client"],
    ["boutons", "Page NFC & boutons"],
    ["stats", "Statistiques"],
    ["notes", `Notes internes (${client.notes.length})`],
  ];

  return (
    <AdminLayout>
      <Link to="/admin/clients" className="inline-flex items-center gap-1.5 text-sm font-semibold text-steel hover:text-electric" data-testid="back-to-clients">
        <ArrowLeft className="h-4 w-4" /> Tous les clients
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-nuit-900 sm:text-3xl">{client.business_name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-steel">
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${client.status === "actif" ? "bg-emerald-100 text-emerald-800" : client.status === "inactif" ? "bg-amber-100 text-amber-800" : "bg-nuit-900/10 text-steel"}`}>
              {STATUS_LABEL[client.status]}
            </span>
            {!client.page_enabled && <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-bold text-red-700">Page désactivée</span>}
            <span>Fiche créée le {new Date(client.created_at).toLocaleDateString("fr-FR")}</span>
            {client.card_ref && <span>· Carte {client.card_ref}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full border border-nuit-900/15 bg-white py-1.5 pl-4 pr-1.5">
            <span className="max-w-52 truncate font-mono text-xs text-nuit-900">{client.public_url}</span>
            <button onClick={copyUrl} data-testid="copy-public-url"
              className="flex items-center gap-1 rounded-full bg-nuit-900 px-3 py-1.5 text-[11px] font-semibold text-white">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copié" : "Copier"}
            </button>
          </div>
          <a href={`/p/${client.slug}`} target="_blank" rel="noopener noreferrer" data-testid="open-public-page"
            className="flex items-center gap-1.5 rounded-full bg-electric px-4 py-2.5 font-display text-xs font-semibold text-white hover:bg-electric-600">
            <ExternalLink className="h-3.5 w-3.5" /> Ouvrir la page
          </a>
        </div>
      </div>

      {(saved || error) && (
        <p className={`mt-5 rounded-xl p-3.5 text-sm font-medium ${saved ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}
          role={error ? "alert" : "status"} data-testid="client-feedback">
          {saved || error}
        </p>
      )}
      {tempPwd && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="temp-password-box">
          <p className="text-sm text-amber-800">
            <strong>Nouveau mot de passe provisoire :</strong> <span className="font-mono">{tempPwd}</span> — affiché une seule fois.
          </p>
          <button onClick={() => setTempPwd("")} className="text-xs font-semibold text-amber-700">Fermer</button>
        </div>
      )}

      <div className="mt-6 flex gap-1.5 overflow-x-auto border-b border-nuit-900/10" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 font-display text-sm font-semibold transition-colors ${
              tab === id ? "border-electric text-nuit-900" : "border-transparent text-steel hover:text-nuit-900"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "fiche" && <FicheTab client={client} patch={patch} setStatus={setStatus} resetAccess={resetAccess} />}
        {tab === "boutons" && <ButtonsTab client={client} patch={patch} />}
        {tab === "stats" && <StatsTab clientId={client.client_id} />}
        {tab === "notes" && <NotesTab client={client} reload={load} />}
      </div>
    </AdminLayout>
  );
}

function FicheTab({ client, patch, setStatus, resetAccess }) {
  const [form, setForm] = useState({ ...client });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) return alert("Logo trop lourd : 600 Ko maximum.");
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
        <h2 className="font-display text-base font-bold text-nuit-900">Informations</h2>
        <div className="mt-4 grid gap-3.5 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Nom du commerce</label>
            <input className={inputCls} value={form.business_name} onChange={set("business_name")} data-testid="fiche-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Contact</label>
            <input className={inputCls} value={form.contact_name} onChange={set("contact_name")} data-testid="fiche-contact" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">E-mail</label>
            <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="fiche-email" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Téléphone</label>
            <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="fiche-phone" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Adresse professionnelle</label>
            <input className={inputCls} value={form.address} onChange={set("address")} data-testid="fiche-address" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Référence carte NFC</label>
            <input className={inputCls} value={form.card_ref} onChange={set("card_ref")} data-testid="fiche-cardref" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Logo (PNG/JPG, 600 Ko max)</label>
            <div className="flex items-center gap-3">
              {form.logo && <img src={form.logo} alt="Logo du commerce" className="h-10 w-10 rounded-xl object-cover" />}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="text-xs text-steel" data-testid="fiche-logo" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-nuit-900">Description de la page</label>
            <textarea rows={2} maxLength={500} className={`${inputCls} resize-y`} value={form.description} onChange={set("description")} data-testid="fiche-description" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-x-7 gap-y-3">
          <label className="flex items-center gap-2.5 text-sm font-medium text-nuit-900">
            <input type="checkbox" checked={form.vcard_enabled} onChange={set("vcard_enabled")} className="h-4 w-4 accent-electric" data-testid="fiche-vcard" />
            Bouton « Enregistrer le contact » (vCard)
          </label>
        </div>
        <button onClick={() => patch(form)} data-testid="fiche-save"
          className="mt-6 rounded-full bg-electric px-6 py-3 font-display text-sm font-semibold text-white hover:bg-electric-600">
          Enregistrer la fiche
        </button>
      </section>

      <div className="space-y-6">
        <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
          <h2 className="font-display text-base font-bold text-nuit-900">Droits du commerçant</h2>
          <p className="mt-1 text-xs text-steel">Ce que le commerçant peut modifier dans son espace.</p>
          <div className="mt-4 space-y-2.5">
            {[["links", "Liens (URL et textes des boutons)"], ["buttons", "Activation et ordre des boutons"], ["description", "Description du commerce"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-nuit-900">
                <input type="checkbox" checked={!!form.editable?.[k]} className="h-4 w-4 accent-electric"
                  onChange={(e) => setForm((f) => ({ ...f, editable: { ...f.editable, [k]: e.target.checked } }))}
                  data-testid={`editable-${k}`} />
                {l}
              </label>
            ))}
            <button onClick={() => patch({ editable: form.editable }, "Droits mis à jour.")} data-testid="editable-save"
              className="mt-2 rounded-full border border-nuit-900/20 px-4 py-2 text-xs font-semibold text-nuit-900 hover:border-nuit-900/50">
              Enregistrer les droits
            </button>
          </div>
        </section>

        <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
          <h2 className="font-display text-base font-bold text-nuit-900">Contrôles</h2>
          <div className="mt-4 space-y-3">
            <label className="flex items-start gap-2.5 text-sm text-nuit-900">
              <input type="checkbox" checked={form.page_enabled}
                onChange={(e) => {
                  if (!e.target.checked && !window.confirm("Désactiver la page publique ? La carte NFC déjà distribuée affichera une page d'indisponibilité. Cette action est réversible.")) {
                    e.preventDefault();
                    return;
                  }
                  patch({ page_enabled: e.target.checked }, e.target.checked ? "Page réactivée." : "Page désactivée (réversible).");
                }}
                className="mt-0.5 h-4 w-4 accent-electric" data-testid="page-enabled-toggle" />
              <span>Page NFC en ligne <span className="block text-xs font-normal text-steel">La désactivation demande toujours confirmation et ne supprime rien.</span></span>
            </label>
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={resetAccess} data-testid="reset-access-btn"
                className="flex items-center gap-1.5 rounded-full border border-nuit-900/20 px-4 py-2 text-xs font-semibold text-nuit-900 hover:border-nuit-900/50">
                <KeyRound className="h-3.5 w-3.5" /> Réinitialiser l'accès
              </button>
              {client.status === "actif" && (
                <button onClick={() => setStatus("inactif")} data-testid="deactivate-btn"
                  className="rounded-full border border-amber-300 px-4 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                  Désactiver le compte
                </button>
              )}
              {client.status !== "actif" && (
                <button onClick={() => setStatus("actif")} data-testid="reactivate-btn"
                  className="rounded-full border border-emerald-300 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                  Réactiver
                </button>
              )}
              {client.status !== "archive" && (
                <button onClick={() => setStatus("archive")} data-testid="archive-btn"
                  className="rounded-full border border-nuit-900/20 px-4 py-2 text-xs font-semibold text-steel hover:bg-brume">
                  Archiver
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ButtonsTab({ client, patch }) {
  const [buttons, setButtons] = useState(client.buttons);
  useEffect(() => setButtons(client.buttons), [client.buttons]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
        <h2 className="font-display text-base font-bold text-nuit-900">Boutons de la page</h2>
        <p className="mt-1 text-xs leading-relaxed text-steel">
          Les liens sont vérifiés à l'enregistrement (https, tel: ou mailto: uniquement). L'URL de la
          page ne change jamais, même quand les liens évoluent.
        </p>
        <div className="mt-5">
          <ButtonsEditor buttons={buttons} onChange={setButtons} />
        </div>
        <button onClick={() => patch({ buttons }, "Boutons enregistrés et publiés.")} data-testid="buttons-save"
          className="mt-6 rounded-full bg-electric px-6 py-3 font-display text-sm font-semibold text-white hover:bg-electric-600">
          Enregistrer et publier
        </button>
      </section>
      <div>
        <PagePreview page={{ ...client, buttons }} />
      </div>
    </div>
  );
}

function StatsTab({ clientId }) {
  const [from, setFrom] = useState(dayPlus(-6));
  const [to, setTo] = useState(dayPlus(0));
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api(`/admin/clients/${clientId}/stats?date_from=${from}&date_to=${to}`).then(setStats).catch(() => {});
  }, [clientId, from, to]);

  return (
    <section className="rounded-3xl border border-nuit-900/10 bg-white p-6" data-testid="client-stats">
      <PeriodPicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      {stats && (
        <>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <StatCard label="Consultations de la page" value={stats.views} sub={`du ${stats.from} au ${stats.to}`} />
            <StatCard label="Clics sur les boutons" value={stats.clicks} sub="un clic ≠ une action accomplie chez le service tiers" />
          </div>
          <div className="mt-6"><Bars series={stats.series} /></div>
          <h3 className="mt-7 font-display text-sm font-bold text-nuit-900">Par bouton</h3>
          <ul className="mt-3 space-y-2">
            {stats.per_button.length === 0 && <li className="text-sm text-steel">Aucun clic sur la période.</li>}
            {stats.per_button.map((b) => (
              <li key={b.button} className="flex items-center justify-between rounded-xl bg-brume px-4 py-2.5 text-sm">
                <span className="font-medium text-nuit-900">{b.label}</span>
                <span className="font-display font-bold text-electric">{b.clicks}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function NotesTab({ client, reload }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api(`/admin/clients/${client.client_id}/notes`, { method: "POST", body: { text } });
      setText("");
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    await api(`/admin/notes/${id}`, { method: "DELETE" }).catch(() => {});
    reload();
  };

  return (
    <section className="max-w-2xl rounded-3xl border border-nuit-900/10 bg-white p-6">
      <h2 className="font-display text-base font-bold text-nuit-900">Notes internes</h2>
      <p className="mt-1 text-xs text-steel">Visibles uniquement par l'administrateur — jamais par le commerçant.</p>
      <form onSubmit={add} className="mt-4 flex gap-2.5">
        <input className={inputCls} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
          placeholder="Ex : carte remise en main propre le 12/08…" data-testid="note-input" />
        <button type="submit" disabled={!text.trim()} data-testid="note-add"
          className="shrink-0 rounded-full bg-nuit-900 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40">
          Ajouter
        </button>
      </form>
      {error && <p className="mt-3 text-sm font-medium text-red-700">{error}</p>}
      <ul className="mt-5 space-y-2.5">
        {client.notes.length === 0 && <li className="text-sm text-steel">Aucune note pour ce client.</li>}
        {client.notes.map((n) => (
          <li key={n.id} className="flex items-start justify-between gap-3 rounded-xl bg-brume px-4 py-3" data-testid={`note-${n.id}`}>
            <div>
              <p className="text-sm text-nuit-900">{n.text}</p>
              <p className="mt-1 text-xs text-steel">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
            </div>
            <button onClick={() => remove(n.id)} aria-label="Supprimer la note" data-testid={`note-del-${n.id}`}
              className="text-steel transition-colors hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
